/**
 * Raport zarządu — teksty sekcji close (`mbr.close.*`). WŁAŚCICIEL: agent sekcji close (docs/RAPORT-ZARZADU.md).
 * Podpięte już w i18n/mbr.ts — dopisuj klucze tutaj, nie w extra.ts. `en` typowane względem `pl`.
 * Własne kody uzgodnień sekcji: `checks: { KOD: '…' }` (panel szuka `mbr.checks.KOD`, potem `mbr.close.checks.KOD`).
 */
import { mbrCloseFieldsEn, mbrCloseFieldsPl } from './mbr-close-fields';
import { mbrCloseUiEn, mbrCloseUiPl, mbrSettingsUiEn, mbrSettingsUiPl } from './mbr-close-ui';

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const mbrClosePl = {
  ...mbrCloseFieldsPl,
  ui: mbrCloseUiPl,
  settingsUi: mbrSettingsUiPl,
  title: 'Zamknięcie miesiąca',
  description:
    'Formularze działów z terminem i osobą odpowiedzialną, import Excela, uzgodnienia, zamrożenie i wysyłka.',
  forms: {
    warehouse: 'Magazyn',
    quality: 'Jakość (reklamacje, utylizacja)',
    production: 'Produkcja',
    fleet: 'Flota',
    drivers: 'Kierowcy (godziny, L4, urlop)',
    costs: 'Koszty (księgowość)',
    hr: 'Kadry i płace',
    receivables: 'Należności i faktoring',
    collections: 'Windykacja',
  },
  formStatus: {
    PENDING: 'Do uzupełnienia',
    IN_PROGRESS: 'W trakcie',
    SUBMITTED: 'Przekazane',
    APPROVED: 'Zatwierdzone',
    REJECTED: 'Do poprawy',
  },
  status: { DRAFT: 'Szkic', IN_REVIEW: 'W przeglądzie', CLOSED: 'Zamrożony' },
  source: { MANUAL: 'ręcznie', IMPORT: 'z importu Excela', SYSTEM: 'z systemu' },
  actions: {
    open: 'Otwórz zamknięcie',
    review: 'Rozpocznij przegląd',
    reconcile: 'Uruchom uzgodnienia',
    freeze: 'Zamroź miesiąc',
    reopen: 'Otwórz ponownie',
    import: 'Importuj Excel',
    fill: 'Uzupełnij',
    view: 'Podgląd',
    approve: 'Zatwierdź',
    reject: 'Zwróć do poprawy',
    submit: 'Przekaż do zatwierdzenia',
    save: 'Zapisz',
    addRow: 'Dodaj wiersz',
    removeRow: 'Usuń wiersz',
    assign: 'Odpowiedzialny i termin',
    cancel: 'Anuluj',
    comment: 'Dodaj komentarz',
    goToForm: 'Przejdź do formularza',
    goToSection: 'Przejdź do sekcji',
  },
  head: { form: 'Formularz', responsible: 'Odpowiedzialny', due: 'Termin', status: 'Status' },
  summary: {
    approved: '{approved} z {total} formularzy zatwierdzonych',
    overdue: '{count} po terminie',
    checks: '{fail} rozbieżności · {warn} do sprawdzenia',
    lastRun: 'Ostatni zapisany przebieg uzgodnień: {date}',
    frozen:
      'Zamrożony {date}{by}. Raport tego miesiąca pokazuje migawkę — zmiany wymagają ponownego otwarcia.',
    frozenBy: ' przez {name}',
    reopened: 'Miesiąc otwarty ponownie {date}.',
    draft: 'Zamknięcie jeszcze nie otwarte — formularze powstaną przy pierwszym zapisie albo po otwarciu.',
  },
  overdue: 'po terminie',
  noResponsible: 'brak odpowiedzialnego',
  noDue: 'bez terminu',
  filled: 'dane wpisane',
  empty: 'brak danych',
  readonly: 'Tylko podgląd — formularz wypełnia osoba odpowiedzialna.',
  prefilled: 'Wartości podpowiedziane z danych systemu lub importu — sprawdź i zapisz.',
  previous: 'Poprzedni miesiąc',
  note: 'Uwagi działu',
  notePlaceholder: 'Np. faktura dojdzie później, kwota szacunkowa…',
  rejectedBecause: 'Zwrócone do poprawy: {reason}',
  checkGroups: {
    FAIL: 'Rozbieżności — blokują zamrożenie',
    WARN: 'Do sprawdzenia',
    INFO: 'Informacje',
    OK: 'Zgodne',
  },
  where: {
    form: 'Formularz: {form}',
    excel: 'Excel: {sheet} {cell}',
    period: 'okres {period}',
    import: 'z importu Excela',
  },
  checks: {
    FORMS_NOT_APPROVED: 'Niezatwierdzone formularze: {count} z {total}.',
    FORM_OVERDUE: 'Formularz po terminie.',
    SECTION_UNAVAILABLE: 'Sekcja „{section}” nie policzyła się — sprawdź dane źródłowe.',
    IMPORT_VS_SYSTEM:
      'Wartość z Excela ({actual}) różni się od policzonej przez system ({expected}) o {diff}.',
    IMPORT_TOTAL_MISMATCH: 'Suma w Excelu ({expected}) nie zgadza się z sumą pozycji ({actual}).',
    IMPORT_CELL_INVALID: 'Komórka nie jest liczbą — wartość pominięta (brak danych, nie zero).',
    IMPORT_BLOCK_MISSING: 'Nie znaleziono bloku „{block}” w pliku.',
  },
  freeze: {
    title: 'Zamrozić miesiąc {period}?',
    description:
      'Raport tego miesiąca zostanie zapisany jako migawka (wszystkie sekcje i tryby porównania) i będzie tylko do odczytu.',
    failures: '{count} rozbieżności blokuje zamrożenie.',
    ack: 'Świadomie zatwierdzam rozbieżności',
    justification: 'Uzasadnienie (trafi do komentarzy i audytu)',
    justificationPlaceholder: 'Np. różnica tras wyjaśniona z księgowością — korekty poza trasą.',
    minChars: 'Co najmniej {n} znaków.',
    confirm: 'Zamroź',
  },
  reopen: {
    title: 'Otworzyć ponownie miesiąc {period}?',
    description: 'Migawka zostaje w historii, raport znowu liczy się na żywo. Zdarzenie trafi do audytu.',
    reason: 'Powód',
    confirm: 'Otwórz ponownie',
  },
  reject: { title: 'Zwrócić formularz do poprawy?', reason: 'Co trzeba poprawić' },
  assign: {
    title: 'Odpowiedzialny i termin',
    responsible: 'Osoba odpowiedzialna',
    due: 'Termin',
    none: '— brak —',
  },
  comments: {
    title: 'Komentarze',
    placeholder: 'Komentarz do miesiąca albo do formularza…',
    empty: 'Brak komentarzy.',
    management: 'Komentarz zarządczy',
    target: { FREEZE_OVERRIDE: 'Uzasadnienie zamrożenia', REOPEN: 'Powód ponownego otwarcia' },
  },
  import: {
    title: 'Import Excela klienta',
    description:
      'Wczytaj obecny plik „raport miesiąc vs miesiąc”. Najpierw podgląd: co zostało rozpoznane, dokąd trafi i jakie rozbieżności znaleziono.',
    choose: 'Wybierz plik .xlsx',
    analyzing: 'Analizuję plik…',
    year: 'Rok w pliku: {year}',
    sheets: 'Arkusze',
    notRecognized: 'nie rozpoznany',
    periods: 'Okresy do zapisu',
    closed: 'zamrożony — pominięty',
    summaryLine: '{forms} formularzy · {metrics} wskaźników · {employees} pracowników',
    warnings: 'Rozbieżności w pliku',
    fields: 'Mapowanie pól ({count})',
    errors: '{count} pól nieczytelnych',
    commit: 'Zapisz do zamknięcia',
    done: 'Zapisano okresy: {periods}',
    noPeriods: 'Wybierz co najmniej jeden okres.',
  },
  toast: {
    saved: 'Formularz zapisany',
    submitted: 'Formularz przekazany do zatwierdzenia',
    approved: 'Formularz zatwierdzony',
    rejected: 'Formularz zwrócony do poprawy',
    assigned: 'Zapisano odpowiedzialnego i termin',
    frozen: 'Miesiąc zamrożony',
    reopened: 'Miesiąc otwarty ponownie',
    reconciled: 'Uzgodnienia przeliczone i zapisane',
    opened: 'Zamknięcie otwarte — odpowiedzialni dostali powiadomienia',
    comment: 'Komentarz dodany',
  },
  errors: {
    generic: 'Nie udało się — spróbuj ponownie.',
    MBR_FORM_INVALID: 'Formularz ma błędy — popraw zaznaczone pola.',
    MBR_CLOSE_BLOCKED: 'Rozbieżności blokują zamrożenie — zatwierdź je świadomie z uzasadnieniem.',
    MBR_CLOSE_FROZEN: 'Miesiąc jest zamrożony — najpierw otwórz go ponownie.',
    MBR_FORM_FORBIDDEN: 'Nie odpowiadasz za ten formularz.',
    MBR_FORM_APPROVED: 'Formularz jest już zatwierdzony.',
    MBR_IMPORT_UNREADABLE: 'To nie jest czytelny plik .xlsx.',
    MBR_CLOSE_FULL_VIEW_REQUIRED: 'Zamrożenie wymaga pełnego dostępu do raportu.',
    MBR_RESPONSIBLE_INVALID: 'Ta osoba nie może wprowadzać danych raportu.',
  },
};

export const mbrCloseEn: Shape<typeof mbrClosePl> = {
  ...mbrCloseFieldsEn,
  ui: mbrCloseUiEn,
  settingsUi: mbrSettingsUiEn,
  title: 'Month close',
  description: 'Department forms with due date and owner, Excel import, reconciliation, freeze and send.',
  forms: {
    warehouse: 'Warehouse',
    quality: 'Quality (complaints, disposal)',
    production: 'Production',
    fleet: 'Fleet',
    drivers: 'Drivers (hours, sick leave, holiday)',
    costs: 'Costs (accounting)',
    hr: 'HR & payroll',
    receivables: 'Receivables & factoring',
    collections: 'Debt collection',
  },
  formStatus: {
    PENDING: 'To fill in',
    IN_PROGRESS: 'In progress',
    SUBMITTED: 'Submitted',
    APPROVED: 'Approved',
    REJECTED: 'To correct',
  },
  status: { DRAFT: 'Draft', IN_REVIEW: 'In review', CLOSED: 'Frozen' },
  source: { MANUAL: 'manual', IMPORT: 'from the Excel import', SYSTEM: 'from the system' },
  actions: {
    open: 'Open the close',
    review: 'Start review',
    reconcile: 'Run reconciliation',
    freeze: 'Freeze the month',
    reopen: 'Reopen',
    import: 'Import Excel',
    fill: 'Fill in',
    view: 'View',
    approve: 'Approve',
    reject: 'Return for correction',
    submit: 'Submit for approval',
    save: 'Save',
    addRow: 'Add row',
    removeRow: 'Remove row',
    assign: 'Owner and due date',
    cancel: 'Cancel',
    comment: 'Add comment',
    goToForm: 'Go to form',
    goToSection: 'Go to section',
  },
  head: { form: 'Form', responsible: 'Owner', due: 'Due', status: 'Status' },
  summary: {
    approved: '{approved} of {total} forms approved',
    overdue: '{count} overdue',
    checks: '{fail} discrepancies · {warn} to check',
    lastRun: 'Last stored reconciliation run: {date}',
    frozen: 'Frozen on {date}{by}. The report of this month shows the snapshot — changes need a reopen.',
    frozenBy: ' by {name}',
    reopened: 'Month reopened on {date}.',
    draft: 'The close is not open yet — forms are created on the first save or when it is opened.',
  },
  overdue: 'overdue',
  noResponsible: 'no owner',
  noDue: 'no due date',
  filled: 'data entered',
  empty: 'no data',
  readonly: 'View only — the form is filled in by its owner.',
  prefilled: 'Values suggested from system or import data — check and save.',
  previous: 'Previous month',
  note: 'Department notes',
  notePlaceholder: 'E.g. the invoice comes later, estimated amount…',
  rejectedBecause: 'Returned for correction: {reason}',
  checkGroups: {
    FAIL: 'Discrepancies — block the freeze',
    WARN: 'To check',
    INFO: 'Information',
    OK: 'Consistent',
  },
  where: {
    form: 'Form: {form}',
    excel: 'Excel: {sheet} {cell}',
    period: 'period {period}',
    import: 'from the Excel import',
  },
  checks: {
    FORMS_NOT_APPROVED: 'Forms not approved: {count} of {total}.',
    FORM_OVERDUE: 'Form overdue.',
    SECTION_UNAVAILABLE: 'Section “{section}” failed to compute — check the source data.',
    IMPORT_VS_SYSTEM: 'Excel value ({actual}) differs from the system value ({expected}) by {diff}.',
    IMPORT_TOTAL_MISMATCH: 'Excel total ({expected}) does not match the sum of the items ({actual}).',
    IMPORT_CELL_INVALID: 'Cell is not a number — value skipped (missing, not zero).',
    IMPORT_BLOCK_MISSING: 'Block “{block}” not found in the file.',
  },
  freeze: {
    title: 'Freeze {period}?',
    description:
      'The report of this month is stored as a snapshot (every section and comparison) and becomes read-only.',
    failures: '{count} discrepancies block the freeze.',
    ack: 'I consciously approve the discrepancies',
    justification: 'Justification (goes to comments and audit)',
    justificationPlaceholder: 'E.g. route difference explained with accounting — corrections outside routes.',
    minChars: 'At least {n} characters.',
    confirm: 'Freeze',
  },
  reopen: {
    title: 'Reopen {period}?',
    description: 'The snapshot stays in history, the report computes live again. The event is audited.',
    reason: 'Reason',
    confirm: 'Reopen',
  },
  reject: { title: 'Return the form for correction?', reason: 'What needs fixing' },
  assign: { title: 'Owner and due date', responsible: 'Owner', due: 'Due date', none: '— none —' },
  comments: {
    title: 'Comments',
    placeholder: 'Comment on the month or on a form…',
    empty: 'No comments.',
    management: 'Management comment',
    target: { FREEZE_OVERRIDE: 'Freeze justification', REOPEN: 'Reopen reason' },
  },
  import: {
    title: 'Client Excel import',
    description:
      'Load the current “month vs month” file. Preview first: what was recognised, where it goes and which discrepancies were found.',
    choose: 'Choose an .xlsx file',
    analyzing: 'Analysing the file…',
    year: 'Year in the file: {year}',
    sheets: 'Sheets',
    notRecognized: 'not recognised',
    periods: 'Periods to write',
    closed: 'frozen — skipped',
    summaryLine: '{forms} forms · {metrics} metrics · {employees} employees',
    warnings: 'Discrepancies in the file',
    fields: 'Field mapping ({count})',
    errors: '{count} unreadable fields',
    commit: 'Write into the close',
    done: 'Periods written: {periods}',
    noPeriods: 'Choose at least one period.',
  },
  toast: {
    saved: 'Form saved',
    submitted: 'Form submitted for approval',
    approved: 'Form approved',
    rejected: 'Form returned for correction',
    assigned: 'Owner and due date saved',
    frozen: 'Month frozen',
    reopened: 'Month reopened',
    reconciled: 'Reconciliation recomputed and stored',
    opened: 'Close opened — owners were notified',
    comment: 'Comment added',
  },
  errors: {
    generic: 'Something went wrong — please try again.',
    MBR_FORM_INVALID: 'The form has errors — fix the marked fields.',
    MBR_CLOSE_BLOCKED: 'Discrepancies block the freeze — approve them consciously with a justification.',
    MBR_CLOSE_FROZEN: 'The month is frozen — reopen it first.',
    MBR_FORM_FORBIDDEN: 'You are not the owner of this form.',
    MBR_FORM_APPROVED: 'The form is already approved.',
    MBR_IMPORT_UNREADABLE: 'This is not a readable .xlsx file.',
    MBR_CLOSE_FULL_VIEW_REQUIRED: 'Freezing needs full access to the report.',
    MBR_RESPONSIBLE_INVALID: 'This person cannot enter report data.',
  },
};
